/**
 * Schulden-Dashboard
 * Aggregierte Übersicht aller Verbindlichkeiten.
 * Klick auf Klient → Detailansicht mit allen Schulden.
 */

import { useState, useEffect } from 'react';
import {
  Scale, AlertTriangle, TrendingDown, Users, Building2,
  ChevronRight, Clock, ArrowLeft, Calendar,
  BarChart3, Loader2, RefreshCw, FileText, Phone,
  AlertCircle, ArrowUpRight, CreditCard, Banknote,
  ChevronDown, ChevronUp, ExternalLink,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import { useTabs } from '@/contexts/TabContext';

const BACKEND_URL = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_BACKEND_URL || '');

const STATUS_BADGE = {
  offen: 'bg-amber-100 text-amber-800',
  in_verhandlung: 'bg-blue-100 text-blue-800',
  ratenzahlung: 'bg-violet-100 text-violet-800',
  mahnung: 'bg-orange-100 text-orange-800',
  inkasso: 'bg-red-100 text-red-800',
  tituliert: 'bg-red-200 text-red-900',
  beglichen: 'bg-green-100 text-green-800',
};

const TYP_ICON = {
  bank: CreditCard,
  inkasso: AlertTriangle,
  behoerde: Building2,
  privat: Users,
  sonstig: FileText,
};

function fmt(n) {
  return (n || 0).toLocaleString('de-DE', { minimumFractionDigits: 2 });
}


// ==================== KLIENT DETAIL ANSICHT ====================

function KlientSchuldenDetail({ klientId, klientName, onBack }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(new Set());
  const { openTab } = useTabs();

  useEffect(() => {
    setLoading(true);
    fetch(`${BACKEND_URL}/api/buchhaltung/schulden-dashboard/klient/${klientId}`)
      .then(r => r.json())
      .then(setData)
      .catch(() => toast.error('Schuldendetails konnten nicht geladen werden'))
      .finally(() => setLoading(false));
  }, [klientId]);

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-amber-500" /></div>;
  if (!data || data.fehler) return <p className="text-center text-red-500 py-8">{data?.fehler || 'Nicht verfügbar'}</p>;

  const { zusammenfassung: zf, verbindlichkeiten, letzte_zahlungen } = data;

  const toggleExpand = (id) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  return (
    <div className="space-y-6" data-testid="klient-schulden-detail">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={onBack} data-testid="schulden-back-btn">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1">
          <h2 className="text-xl font-bold text-slate-800">{data.klient_name || klientName}</h2>
          <p className="text-sm text-slate-500">{zf.anzahl_offen} offene Verbindlichkeiten bei {zf.anzahl_glaeubiger} Gläubigern</p>
        </div>
        <Button variant="outline" size="sm" onClick={() => openTab(`/akten/${klientId}`, data.klient_name)}>
          <ExternalLink className="w-4 h-4 mr-1" /> Akte
        </Button>
      </div>

      {/* Zusammenfassung */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Card className="bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200">
          <CardContent className="pt-3 pb-3">
            <p className="text-xs text-amber-600">Gesamtschulden</p>
            <p className="text-xl font-bold text-amber-800">{fmt(zf.gesamt_schulden)} €</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-3 pb-3">
            <p className="text-xs text-green-600">Bereits getilgt</p>
            <p className="text-xl font-bold text-green-700">{fmt(zf.bereits_getilgt)} €</p>
            <Progress value={zf.tilgung_prozent} className="h-1.5 mt-1" />
          </CardContent>
        </Card>
        {zf.ueberfaellig > 0 && (
          <Card className="border-red-200 bg-red-50">
            <CardContent className="pt-3 pb-3">
              <p className="text-xs text-red-600 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Überfällig</p>
              <p className="text-xl font-bold text-red-700">{fmt(zf.ueberfaellig)} €</p>
            </CardContent>
          </Card>
        )}
        <Card>
          <CardContent className="pt-3 pb-3">
            <p className="text-xs text-slate-500">Beglichen</p>
            <p className="text-xl font-bold text-slate-700">{zf.anzahl_beglichen}</p>
            <p className="text-xs text-slate-400 mt-1">abgeschlossene Schulden</p>
          </CardContent>
        </Card>
      </div>

      {/* Verbindlichkeiten Liste */}
      <div className="space-y-3">
        <h3 className="font-semibold text-slate-700 flex items-center gap-2">
          <Scale className="w-5 h-5 text-amber-600" />
          Offene Verbindlichkeiten ({verbindlichkeiten.length})
        </h3>
        {verbindlichkeiten.length === 0 ? (
          <p className="text-slate-400 text-center py-6">Keine offenen Verbindlichkeiten</p>
        ) : (
          verbindlichkeiten.map((v) => {
            const isExpanded = expanded.has(v.id);
            const Icon = TYP_ICON[v.glaeubiger_typ] || FileText;

            return (
              <Card
                key={v.id}
                className={`transition-all ${v.ist_ueberfaellig ? 'border-red-200 bg-red-50/30' : 'hover:border-slate-300'}`}
                data-testid={`verbindlichkeit-${v.id}`}
              >
                <div
                  className="p-4 cursor-pointer"
                  onClick={() => toggleExpand(v.id)}
                >
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${v.ist_ueberfaellig ? 'bg-red-100 text-red-600' : 'bg-slate-100 text-slate-600'}`}>
                        <Icon className="w-5 h-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-slate-800 truncate">{v.bezeichnung}</p>
                        <p className="text-sm text-slate-500">{v.glaeubiger_name}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0">
                      <div className="text-right">
                        <p className="text-lg font-bold text-amber-700">{fmt(v.aktueller_stand)} €</p>
                        {v.ursprungsbetrag !== v.aktueller_stand && (
                          <p className="text-xs text-slate-400 line-through">{fmt(v.ursprungsbetrag)} €</p>
                        )}
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        <Badge className={`text-xs ${STATUS_BADGE[v.status] || STATUS_BADGE.offen}`}>
                          {v.status}
                        </Badge>
                        {v.ist_ueberfaellig && (
                          <Badge variant="destructive" className="text-xs">
                            {v.tage_ueberfaellig}d überfällig
                          </Badge>
                        )}
                      </div>
                      {isExpanded ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
                    </div>
                  </div>

                  {/* Schnell-Info Zeile */}
                  <div className="flex flex-wrap gap-3 mt-2 text-xs text-slate-500">
                    {v.ratenzahlung && (
                      <span className="flex items-center gap-1 text-violet-600">
                        <Banknote className="w-3 h-3" />
                        Rate: {fmt(v.rate_betrag)} € / {v.rate_rhythmus || 'Monat'}
                      </span>
                    )}
                    {v.inkasso && (
                      <span className="flex items-center gap-1 text-red-600">
                        <AlertTriangle className="w-3 h-3" />
                        Inkasso: {v.inkasso_buero || 'Ja'}
                      </span>
                    )}
                    {v.mahnstufe > 0 && (
                      <span className="flex items-center gap-1 text-orange-600">
                        <AlertCircle className="w-3 h-3" />
                        Mahnstufe {v.mahnstufe}
                      </span>
                    )}
                    {v.faelligkeitsdatum && (
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        Fällig: {new Date(v.faelligkeitsdatum).toLocaleDateString('de-DE')}
                      </span>
                    )}
                  </div>
                </div>

                {/* Aufklappbare Details */}
                {isExpanded && (
                  <div className="px-4 pb-4 border-t border-slate-100 pt-3 space-y-3 bg-slate-50/50">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-xs text-slate-400">Gläubiger</p>
                        <p className="font-medium">{v.glaeubiger_name}</p>
                        {v.glaeubiger_aktenzeichen && <p className="text-xs text-slate-500">Az: {v.glaeubiger_aktenzeichen}</p>}
                        {v.glaeubiger_ansprechpartner && <p className="text-xs text-slate-500">Kontakt: {v.glaeubiger_ansprechpartner}</p>}
                      </div>
                      <div>
                        <p className="text-xs text-slate-400">Typ</p>
                        <p className="font-medium capitalize">{v.glaeubiger_typ}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-400">Ursprungsbetrag</p>
                        <p className="font-medium">{fmt(v.ursprungsbetrag)} €</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-400">Aktueller Stand</p>
                        <p className="font-medium text-amber-700">{fmt(v.aktueller_stand)} €</p>
                      </div>
                      {v.ratenzahlung && (
                        <>
                          <div>
                            <p className="text-xs text-slate-400">Ratenzahlung</p>
                            <p className="font-medium text-violet-700">{fmt(v.rate_betrag)} € / {v.rate_rhythmus || 'Monat'}</p>
                          </div>
                          <div>
                            <p className="text-xs text-slate-400">Restlaufzeit (geschätzt)</p>
                            <p className="font-medium">{v.rate_betrag > 0 ? `~${Math.ceil(v.aktueller_stand / v.rate_betrag)} Monate` : 'k.A.'}</p>
                          </div>
                        </>
                      )}
                      {v.inkasso && (
                        <div className="col-span-2">
                          <p className="text-xs text-slate-400">Inkasso-Büro</p>
                          <p className="font-medium text-red-700">{v.inkasso_buero || 'Im Inkasso'}</p>
                        </div>
                      )}
                    </div>
                    {v.notizen && (
                      <div>
                        <p className="text-xs text-slate-400">Notizen</p>
                        <p className="text-sm text-slate-700 bg-white rounded p-2 mt-1">{v.notizen}</p>
                      </div>
                    )}
                    <div className="flex gap-2 pt-1">
                      <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); openTab(`/buchhaltung?klient_id=${klientId}&tab=verbindlichkeiten`, 'Buchhaltung'); }}>
                        <FileText className="w-3 h-3 mr-1" /> In Buchhaltung öffnen
                      </Button>
                    </div>
                  </div>
                )}
              </Card>
            );
          })
        )}
      </div>

      {/* Letzte Zahlungen */}
      {letzte_zahlungen?.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Banknote className="w-5 h-5 text-green-600" />
              Letzte Zahlungen
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {letzte_zahlungen.map((z, i) => (
              <div key={i} className="flex items-center justify-between py-2 text-sm border-b border-slate-50 last:border-0">
                <div>
                  <p className="text-slate-800">{z.bezeichnung || z.verwendungszweck || 'Zahlung'}</p>
                  <p className="text-xs text-slate-500">{z.datum ? new Date(z.datum).toLocaleDateString('de-DE') : ''}</p>
                </div>
                <p className="font-medium text-green-700">-{fmt(z.betrag)} €</p>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}


// ==================== HAUPT-DASHBOARD ====================

export default function SchuldenDashboardPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedKlient, setSelectedKlient] = useState(null);
  const { openTab } = useTabs();

  const loadData = async () => {
    setLoading(true);
    try {
      const result = await fetch(`${BACKEND_URL}/api/buchhaltung/schulden-dashboard`).then(r => r.json());
      setData(result);
    } catch (err) {
      toast.error('Dashboard konnte nicht geladen werden');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // Klient-Detailansicht
  if (selectedKlient) {
    return (
      <div className="p-4 sm:p-6">
        <KlientSchuldenDetail
          klientId={selectedKlient.klient_id}
          klientName={selectedKlient.name}
          onBack={() => setSelectedKlient(null)}
        />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-violet-500" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="w-12 h-12 mx-auto text-slate-300 mb-4" />
        <p className="text-slate-500">Dashboard nicht verfügbar</p>
        <Button onClick={loadData} className="mt-4">
          <RefreshCw className="w-4 h-4 mr-2" /> Erneut laden
        </Button>
      </div>
    );
  }

  const { zusammenfassung, ueberfaellig, nach_glaeubiger, nach_klient, trend } = data;

  return (
    <div className="space-y-6 p-4 sm:p-6" data-testid="schulden-dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <Scale className="w-7 h-7 text-amber-600" />
            Schulden-Dashboard
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            Aggregierte Übersicht aller Verbindlichkeiten
          </p>
        </div>
        <Button variant="outline" onClick={loadData}>
          <RefreshCw className="w-4 h-4 mr-2" /> Aktualisieren
        </Button>
      </div>

      {/* Überfällig-Alert */}
      {ueberfaellig.anzahl > 0 && (
        <div className="bg-red-50 border-l-4 border-red-500 rounded-r-xl p-4" data-testid="ueberfaellig-alert">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-6 h-6 text-red-500 shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="font-semibold text-red-800">
                {ueberfaellig.anzahl} überfällige Verbindlichkeit{ueberfaellig.anzahl !== 1 ? 'en' : ''}
              </h3>
              <p className="text-red-700 text-sm mt-1">
                Gesamtsumme: <span className="font-bold">{fmt(ueberfaellig.summe)} €</span> •
                {ueberfaellig.klienten_betroffen} Klient{ueberfaellig.klienten_betroffen !== 1 ? 'en' : ''} betroffen
              </p>
              {ueberfaellig.details?.slice(0, 3).map((v, i) => (
                <div 
                  key={i} 
                  className="mt-2 text-sm text-red-700 flex items-center gap-2 cursor-pointer hover:text-red-900 hover:underline"
                  onClick={() => setSelectedKlient({ klient_id: v.klient_id, name: v.klient_name })}
                  data-testid={`ueberfaellig-detail-${i}`}
                >
                  <Clock className="w-4 h-4" />
                  <span>{v.klient_name}: {v.bezeichnung} - {fmt(v.betrag)} € ({v.tage_ueberfaellig} Tage überfällig)</span>
                  <ArrowUpRight className="w-3 h-3 shrink-0" />
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Zusammenfassung Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200">
          <CardContent className="pt-4">
            <p className="text-sm text-amber-700 flex items-center gap-1"><Scale className="w-4 h-4" /> Gesamtschulden</p>
            <p className="text-3xl font-bold text-amber-800 mt-1">{fmt(zusammenfassung.gesamt_schulden)} €</p>
            <p className="text-xs text-amber-600 mt-2">Ursprünglich: {fmt(zusammenfassung.gesamt_ursprung)} €</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <p className="text-sm text-green-600 flex items-center gap-1"><TrendingDown className="w-4 h-4" /> Bereits getilgt</p>
            <p className="text-3xl font-bold text-green-700 mt-1">{fmt(zusammenfassung.gesamt_getilgt)} €</p>
            <div className="mt-2">
              <Progress value={zusammenfassung.tilgung_prozent} className="h-2" />
              <p className="text-xs text-slate-500 mt-1">{zusammenfassung.tilgung_prozent}% getilgt</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <p className="text-sm text-slate-500 flex items-center gap-1"><Users className="w-4 h-4" /> Klienten</p>
            <p className="text-3xl font-bold text-slate-800 mt-1">{zusammenfassung.anzahl_klienten}</p>
            <p className="text-xs text-slate-500 mt-2">{zusammenfassung.anzahl_verbindlichkeiten} Verbindlichkeiten</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <p className="text-sm text-slate-500 flex items-center gap-1"><Building2 className="w-4 h-4" /> Gläubiger</p>
            <p className="text-3xl font-bold text-slate-800 mt-1">{zusammenfassung.anzahl_glaeubiger}</p>
            <p className="text-xs text-slate-500 mt-2">mit offenen Forderungen</p>
          </CardContent>
        </Card>
      </div>

      {/* Trend */}
      {trend && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-violet-600" />
              Zahlungen der letzten 6 Monate
            </CardTitle>
            <CardDescription>Gesamt: {fmt(trend.summe_zahlungen_6m)} €</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-end gap-2 h-32">
              {trend.monate.map((m, i) => {
                const maxZ = Math.max(...trend.monate.map(x => x.zahlungen), 1);
                const h = (m.zahlungen / maxZ) * 100;
                return (
                  <div key={i} className="flex-1 flex flex-col items-center">
                    <div className="w-full flex flex-col items-center justify-end h-24">
                      <div className="w-full bg-gradient-to-t from-violet-500 to-violet-400 rounded-t-lg transition-all" style={{ height: `${Math.max(h, 4)}%` }}
                        title={`${fmt(m.zahlungen)} €`} />
                    </div>
                    <p className="text-xs text-slate-500 mt-1">{m.label.split(' ')[0]}</p>
                    <p className="text-xs font-medium text-slate-700">{m.zahlungen >= 1000 ? `${(m.zahlungen / 1000).toFixed(1)}k` : m.zahlungen.toFixed(0)}</p>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Klienten mit Schulden — HAUPTLISTE */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Users className="w-5 h-5 text-amber-600" />
            Betreute mit Schulden
          </CardTitle>
          <CardDescription>Klicken für Detailansicht aller Verbindlichkeiten</CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          {nach_klient?.length > 0 ? nach_klient.map((k, i) => (
            <div
              key={i}
              className="p-4 rounded-xl bg-slate-50 hover:bg-blue-50 transition-all cursor-pointer border border-transparent hover:border-blue-200 group"
              onClick={() => setSelectedKlient(k)}
              data-testid={`klient-schulden-${k.klient_id}`}
            >
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center text-amber-700 font-bold text-sm flex-shrink-0">
                    {i + 1}
                  </div>
                  <div>
                    <p className="font-medium text-slate-800 group-hover:text-blue-800">{k.name}</p>
                    <p className="text-xs text-slate-500">
                      {k.anzahl_glaeubiger} Gläubiger • {k.anzahl_verbindlichkeiten} Verbindlichkeiten
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-right">
                    <p className="text-lg font-bold text-amber-700">{fmt(k.summe_aktuell)} €</p>
                    <div className="flex items-center gap-2">
                      <Progress value={k.tilgung_prozent} className="h-1.5 w-16" />
                      <span className="text-xs text-slate-500">{k.tilgung_prozent.toFixed(0)}%</span>
                    </div>
                  </div>
                  {k.ueberfaellig > 0 && (
                    <Badge variant="destructive" className="text-xs flex-shrink-0">
                      <AlertTriangle className="w-3 h-3 mr-1" />
                      {fmt(k.ueberfaellig)} €
                    </Badge>
                  )}
                  <ChevronRight className="w-5 h-5 text-slate-300 group-hover:text-blue-500 transition-colors" />
                </div>
              </div>
            </div>
          )) : (
            <p className="text-center text-slate-400 py-8">Keine Klienten mit offenen Schulden</p>
          )}
        </CardContent>
      </Card>

      {/* Gläubiger */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Building2 className="w-5 h-5 text-blue-600" />
            Schulden nach Gläubiger
          </CardTitle>
          <CardDescription>Tilgungsprognose pro Gläubiger</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3 max-h-96 overflow-y-auto">
          {nach_glaeubiger?.slice(0, 10).map((g, i) => (
            <div key={i} className="p-3 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-slate-800">{g.name}</p>
                  <p className="text-xs text-slate-500">
                    {g.anzahl_verbindlichkeiten} Verbindlichkeit{g.anzahl_verbindlichkeiten !== 1 ? 'en' : ''} • {g.klienten} Klient{g.klienten !== 1 ? 'en' : ''}
                  </p>
                </div>
                <div className="text-right">
                  <p className="font-bold text-amber-700">{fmt(g.summe_aktuell)} €</p>
                  {g.ueberfaellig > 0 && (
                    <Badge variant="destructive" className="text-xs mt-1">
                      {fmt(g.ueberfaellig)} € überfällig
                    </Badge>
                  )}
                </div>
              </div>
              <div className="mt-2">
                <Progress value={g.tilgung_prozent} className="h-1.5" />
                <div className="flex justify-between text-xs text-slate-500 mt-1">
                  <span>{g.tilgung_prozent.toFixed(0)}% getilgt</span>
                  {g.prognose_tilgung && (
                    <span className="text-violet-600 flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      Tilgung: {new Date(g.prognose_tilgung.datum).toLocaleDateString('de-DE', { month: 'short', year: 'numeric' })}
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
          {nach_glaeubiger?.length === 0 && (
            <p className="text-center text-slate-400 py-4">Keine Gläubiger mit offenen Forderungen</p>
          )}
        </CardContent>
      </Card>

      {/* Footer */}
      <p className="text-xs text-slate-400 text-right">
        Letzte Aktualisierung: {new Date(data.generiert_am).toLocaleString('de-DE')}
      </p>
    </div>
  );
}
